import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Props } from '../@types';
import { AuthContextType, User } from '../@types/auth';
import { decodePayload } from '../utils/decode-payload';
import {
    ACCESS_TOKEN_NAME,
    KEYCLOAK_CLIENT_ID,
    getAuthToken,
    getRefreshToken as getRefreshTokenFromCookie,
    invalidateKeycloakSession,
    KEYCLOAK_ENDPOINT,
    REFRESH_TOKEN_NAME,
    removeAllAuthCookies,
    setCookieByName,
    validateSession,
} from '../utils/authCookies';
import { del, get, set } from 'idb-keyval';

const DefaultUser: User = {
    accessToken: '',
    email: '',
    exp: 0,
    refreshExp: 0,
    hasRole(): boolean {
        return false;
    },
    isAccessExpired(): boolean {
        return true;
    },
    isRefreshTimeExpired(): boolean {
        return true;
    },
    keycloakUserId: '',
    name: '',
    refreshToken: '',
    roles: [],
    username: '',
    workplace: '',
};

const AuthContext = createContext<AuthContextType>({
    user: DefaultUser,
    isAuthenticated: false,
    isAuthenticationInitialized: false,
    signIn: (authToken: string, refreshToken: string) => console.log('signIn'),
    signOut: () => console.log('signOut'),
    setAuthToken: (token: string) => console.log('setToken'),
    setRefreshToken: (token: string) => console.log('setToken'),
    refreshTheToken: () => Promise.any(''),
});

const persistTokens = async (accessToken: string, refreshToken: string) => {
    setCookieByName(ACCESS_TOKEN_NAME, accessToken);
    if (refreshToken) {
        setCookieByName(REFRESH_TOKEN_NAME, refreshToken);
    }
    await set('access-token', accessToken);
    await set('refresh-token', refreshToken);
};

const resolveTokens = async (): Promise<{ accessToken?: string; refreshToken: string }> => {
    const accessToken = getAuthToken() ?? (await get<string>('access-token'));
    const refreshToken = getRefreshTokenFromCookie() ?? (await get<string>('refresh-token')) ?? '';
    return { accessToken, refreshToken };
};

const clearTokens = async () => {
    removeAllAuthCookies();
    await del('access-token');
    await del('refresh-token');
};

const isRefreshTokenExpired = (refreshToken: string): boolean => {
    try {
        const { exp } = decodePayload(refreshToken);
        return new Date().getTime() > exp * 1000;
    } catch {
        return true;
    }
};

const buildUserFromTokens = (accessToken: string, refreshToken: string): User | null => {
    if (!accessToken) {
        return null;
    }

    try {
        const payload = decodePayload(accessToken);

        if (!payload) {
            console.error('no payload');
            return null;
        }

        const refreshPayload = refreshToken ? decodePayload(refreshToken) : null;
        if (refreshToken && !refreshPayload) {
            console.error('no refreshToken payload');
            return null;
        }

        const { exp, preferred_username, family_name, given_name, email, realm_access, workplace, sub } = payload;

        return {
            keycloakUserId: sub,
            username: preferred_username,
            name: `${family_name} ${given_name}`,
            email,
            roles: realm_access.roles,
            accessToken,
            refreshToken,
            exp,
            refreshExp: refreshPayload?.exp ?? 0,
            workplace,
            hasRole: (role: string) => realm_access.roles.some(r => r === role),
            isAccessExpired: () => new Date().getTime() > exp * 1000,
            isRefreshTimeExpired: () =>
                refreshPayload ? new Date().getTime() > refreshPayload.exp * 1000 : true,
        };
    } catch (e) {
        console.error(e);
        return null;
    }
};

const useProvideAuth = () => {
    const [user, setUser] = useState<User>(DefaultUser);
    const [isAuthenticationInitialized, setIsAuthenticationInitialized] = useState<boolean>(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const signOut = async () => {
        invalidateKeycloakSession();
        setUser(DefaultUser);
        await clearTokens();
        await del('schedule-data');
        await del('line-stops');
        window.location.assign('/login');
    };

    const setAccessToken = async (token: string) => {
        setCookieByName(ACCESS_TOKEN_NAME, token);
        await set('access-token', token);
        setUser(prev => ({ ...prev, accessToken: token }));
    };

    const setRefreshToken = async (token: string) => {
        if (token) {
            setCookieByName(REFRESH_TOKEN_NAME, token);
            await set('refresh-token', token);
            setUser(prev => ({ ...prev, refreshToken: token }));
        }
    };

    const hydrateUser = async () => {
        const { accessToken, refreshToken } = await resolveTokens();
        const resolvedUser = buildUserFromTokens(accessToken ?? '', refreshToken);

        if (resolvedUser) {
            setUser(resolvedUser);
        }
    };

    const signIn = async (accessToken: string, refreshToken: string) => {
        const resolvedUser = buildUserFromTokens(accessToken, refreshToken);

        if (!resolvedUser) {
            return;
        }

        await persistTokens(accessToken, refreshToken);
        setUser(resolvedUser);
    };

    const refreshTokens = async (refreshToken: string): Promise<string | undefined> => {
        if (!KEYCLOAK_ENDPOINT || !refreshToken) {
            return;
        }

        if (isRefreshTokenExpired(refreshToken)) {
            await signOut();
            return;
        }

        try {
            const response = await fetch(KEYCLOAK_ENDPOINT, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    client_id: KEYCLOAK_CLIENT_ID,
                    refresh_token: refreshToken,
                    grant_type: 'refresh_token',
                }),
            });

            if (response.status === 401) {
                await signOut();
                return;
            }

            if (!response.ok) {
                return;
            }

            const body = await response.json();
            await signIn(body.access_token, body.refresh_token);
            return body.access_token;
        } catch (e) {
            console.error(e);
            return;
        }
    };

    const refreshTheToken = async (): Promise<string | undefined> => {
        if (!KEYCLOAK_ENDPOINT) {
            return;
        }

        if (user.refreshExp && new Date().getTime() > user.refreshExp * 1000) {
            await signOut();
            return;
        }

        const refreshTime = new Date();
        refreshTime.setMinutes(refreshTime.getMinutes() + 1);

        if (user.exp && refreshTime.getTime() <= user.exp * 1000) {
            return user.accessToken || undefined;
        }

        const { refreshToken } = await resolveTokens();
        return refreshTokens(refreshToken);
    };

    const initializeAuth = async () => {
        const accessFromQuery = searchParams.get('t') ?? searchParams.get(ACCESS_TOKEN_NAME) ?? undefined;
        const refreshFromQuery = searchParams.get('r') ?? searchParams.get(REFRESH_TOKEN_NAME) ?? undefined;
        const hadQueryToken = searchParams.has('t') || searchParams.has('r');

        if (hadQueryToken) {
            const access = accessFromQuery ?? getAuthToken();
            const refresh = refreshFromQuery ?? getRefreshTokenFromCookie() ?? '';

            if (access) {
                await signIn(access, refresh);
                setSearchParams({}, { replace: true });
            }

            setIsAuthenticationInitialized(true);
            return;
        }

        const { accessToken, refreshToken } = await resolveTokens();

        if (navigator.onLine) {
            if (accessToken && (await validateSession(accessToken))) {
                await hydrateUser();
            } else if (refreshToken) {
                const refreshedAccessToken = await refreshTokens(refreshToken);
                if (!refreshedAccessToken) {
                    await clearTokens();
                    setUser(DefaultUser);
                }
            } else {
                await clearTokens();
                setUser(DefaultUser);
            }
        } else if (accessToken && refreshToken && !isRefreshTokenExpired(refreshToken)) {
            await hydrateUser();
        } else if (accessToken && !refreshToken) {
            await hydrateUser();
        }

        setIsAuthenticationInitialized(true);
    };

    useEffect(() => {
        initializeAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        user,
        isAuthenticated: user !== undefined && !!user.accessToken,
        isAuthenticationInitialized,
        signIn,
        signOut,
        setAuthToken: setAccessToken,
        setRefreshToken,
        refreshTheToken,
    };
};

const useAuth = () => {
    return useContext(AuthContext);
};

const ProvideAuth = ({ children }: Props) => {
    const auth = useProvideAuth();

    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export { useAuth, useProvideAuth, ProvideAuth };
